"use client";

import * as React from "react";
import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, FileText, Mail, MapPin, Phone, Scale, ShieldAlert, ShieldCheck, Truck, CreditCard, Building2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const tableOfContents = [{
  id: "site-use",
  number: "01",
  title: "Site use & account security"
}, {
  id: "orders-terms",
  number: "02",
  title: "Orders & commercial terms"
}, {
  id: "pricing-payment",
  number: "03",
  title: "Pricing & payment specifications"
}, {
  id: "shipping-logistics",
  number: "04",
  title: "Shipping & freight logistics"
}, {
  id: "warranty-liability",
  number: "05",
  title: "Warranty & liability limits"
}, {
  id: "dispute-resolution",
  number: "06",
  title: "Dispute resolution & governing law"
}];
export default function Terms() {
  const shouldReduceMotion = useReducedMotion();
  const [activeSection, setActiveSection] = useState("site-use");
  const motionConfig = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    animate: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 15
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.24,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="terms-conditions" data-nav="dark" className="bg-background text-foreground py-12 md:py-20 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb Navigation */}
        <motion.div {...motionConfig} className="mb-8">
          <Breadcrumb>
            <BreadcrumbList className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/HomePage">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <span className="text-muted-foreground">Legal</span>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-primary font-semibold">
                  Terms & Conditions
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </motion.div>

        {/* Section Header with Precision Spec Rule */}
        <motion.div {...motionConfig} className="mb-12 border-b border-border pb-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="border-l-[3px] border-primary pl-4 md:pl-6">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground border-border rounded-xs px-2 py-0.5">
                  Document Ref: HS-LGL-2025-V2
                </Badge>
                <Badge variant="secondary" className="font-mono text-[11px] uppercase tracking-wider text-secondary-foreground rounded-xs px-2 py-0.5">
                  Commercial Policy
                </Badge>
              </div>
              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground">
                Terms & Conditions
              </h1>
            </div>

            <div className="font-mono text-xs text-muted-foreground space-y-1 bg-card/60 border border-border p-3.5 rounded-xs">
              <div className="flex items-center justify-between gap-6">
                <span>Effective revision:</span>
                <span className="text-foreground font-semibold">October 15, 2024</span>
              </div>
              <div className="flex items-center justify-between gap-6">
                <span>Jurisdiction:</span>
                <span className="text-foreground font-semibold">Orange, California, USA</span>
              </div>
              <div className="flex items-center justify-between gap-6">
                <span>Entity:</span>
                <span className="text-foreground font-semibold">Huga Scalet LLC</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Two-Column Grid: TOC & Document Body */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Sticky Table of Contents */}
          <aside className="lg:col-span-4 lg:sticky lg:top-24 space-y-6">
            <Card className="bg-card border border-border rounded-xs shadow-sm">
              <CardHeader className="border-b border-border pb-4">
                <div className="flex items-center gap-2">
                  <FileText className="size-4 text-primary" />
                  <CardTitle className="font-serif text-base uppercase tracking-wide text-card-foreground">
                    Document index
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <nav className="space-y-1.5" aria-label="Table of contents">
                  {tableOfContents.map((item, i) => {
                  const isActive = activeSection === item.id;
                  return <Link key={item.id} data-index={i} to={`#${item.id}`} onClick={() => setActiveSection(item.id)} className={`group flex items-center justify-between p-2.5 text-xs rounded-xs border transition-colors ${isActive ? "bg-muted/70 text-primary border-primary/40 font-medium" : "border-transparent text-muted-foreground hover:bg-muted/40 hover:text-foreground hover:border-border"}`}>
                        <div className="flex items-center gap-2.5">
                          <span className="font-mono text-[11px] text-muted-foreground group-hover:text-primary">
                            {item.number}
                          </span>
                          <span className="truncate">{item.title}</span>
                        </div>
                        <ArrowUpRight className="size-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-primary shrink-0" />
                      </Link>;
                })}
                </nav>
              </CardContent>
            </Card>

            {/* Compliance Quick Box */}
            <Card className="bg-card/40 border border-border rounded-xs p-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" />
                <div className="space-y-1 text-xs">
                  <p className="font-semibold text-foreground">Verified Commercial Standards</p>
                  <p className="text-muted-foreground leading-relaxed">
                    All transactions adhere to California Commercial Code and U.S. consumer retail standards.
                  </p>
                </div>
              </div>
            </Card>
          </aside>

          {/* Right Column: Legal Clauses */}
          <main className="lg:col-span-8 space-y-8">
            {/* Clause 01 */}
            <motion.article id="site-use" {...motionConfig} className="scroll-mt-24 rounded-xs border border-border bg-card p-6 md:p-8">
              <div className="flex items-center justify-between border-b border-border pb-4 mb-5">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm font-bold text-primary">01</span>
                  <h2 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-wide text-foreground">
                    Site use & account security
                  </h2>
                </div>
                <Badge variant="outline" className="font-mono text-[10px] text-muted-foreground border-border">
                  SEC-01
                </Badge>
              </div>

              <div className="space-y-4 text-sm text-muted-foreground leading-relaxed font-sans">
                <p>
                  Welcome to Huga Scalet LLC. By accessing this depot website, purchasing workshop hardware, or
                  registering a commercial user profile, you enter into a legally binding agreement governed by
                  these terms. If you disagree with any provision, cease operations on this site immediately.
                </p>
                <p>
                  You are responsible for maintaining the confidentiality of your account credentials, security
                  tokens, and access passcodes. You agree to accept liability for all activities, tool requisitions,
                  and purchase orders executed under your verified account.
                </p>

                <div className="bg-muted/40 border-l-2 border-primary p-3.5 font-mono text-xs text-foreground space-y-1">
                  <p className="font-bold uppercase tracking-wider text-primary">Requirement note:</p>
                  <p className="text-muted-foreground">
                    Users must be at least 18 years of age or possess authorized corporate purchasing authority to
                    execute transactions.
                  </p>
                </div>
              </div>
            </motion.article>

            {/* Clause 02 */}
            <motion.article id="orders-terms" {...motionConfig} className="scroll-mt-24 rounded-xs border border-border bg-card p-6 md:p-8">
              <div className="flex items-center justify-between border-b border-border pb-4 mb-5">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm font-bold text-primary">02</span>
                  <h2 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-wide text-foreground">
                    Orders & commercial terms
                  </h2>
                </div>
                <Badge variant="outline" className="font-mono text-[10px] text-muted-foreground border-border">
                  SEC-02
                </Badge>
              </div>

              <div className="space-y-4 text-sm text-muted-foreground leading-relaxed font-sans">
                <p>
                  All catalog listings, unit specifications, and pricing tiers across our 33 products and 11
                  equipment categories constitute an invitation to offer. An order submission confirms your intent
                  to buy under stated specifications.
                </p>
                <p>
                  Huga Scalet LLC reserves the right to accept, adjust, or decline orders in cases of stock
                  depletion, typographical pricing errors, or suspected unauthorized freight forwarding. Upon order
                  dispatch, a digital manifest is transmitted confirming receipt.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  <div className="p-3 bg-muted/30 border border-border rounded-xs">
                    <p className="font-mono text-xs font-bold text-foreground mb-1">Catalog Accuracy</p>
                    <p className="text-xs text-muted-foreground">
                      Technical dimensions and duty ratings match manufacturer bench tests.
                    </p>
                  </div>
                  <div className="p-3 bg-muted/30 border border-border rounded-xs">
                    <p className="font-mono text-xs font-bold text-foreground mb-1">Cancellation Window</p>
                    <p className="text-xs text-muted-foreground">
                      Modifications allowed within 60 minutes prior to staging and palletizing.
                    </p>
                  </div>
                </div>
              </div>
            </motion.article>

            {/* Clause 03 */}
            <motion.article id="pricing-payment" {...motionConfig} className="scroll-mt-24 rounded-xs border border-border bg-card p-6 md:p-8">
              <div className="flex items-center justify-between border-b border-border pb-4 mb-5">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm font-bold text-primary">03</span>
                  <h2 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-wide text-foreground">
                    Pricing & payment specifications
                  </h2>
                </div>
                <Badge variant="outline" className="font-mono text-[10px] text-muted-foreground border-border">
                  SEC-03
                </Badge>
              </div>

              <div className="space-y-4 text-sm text-muted-foreground leading-relaxed font-sans">
                <p>
                  Prices are listed in U.S. Dollars (USD) and exclude applicable state and local sales taxes, freight
                  handling, and hazardous material surcharges where applicable. Applicable taxes are computed at
                  checkout based on destination shipping coordinates.
                </p>
                <p>
                  Payment must be authorized in full via approved commercial gateways prior to order release. We
                  employ encrypted 256-bit SSL tokenization. No cardholder data is stored on local facility servers.
                </p>

                <div className="p-4 bg-muted/30 border border-border rounded-xs flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <CreditCard className="size-5 text-primary shrink-0" />
                    <div>
                      <p className="font-mono text-xs font-bold text-foreground">Accepted Tender</p>
                      <p className="text-xs text-muted-foreground">Major credit cards, debit cards, and commercial wire</p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="font-mono text-xs">
                    SSL 256-Bit Encrypted
                  </Badge>
                </div>
              </div>
            </motion.article>

            {/* Clause 04 */}
            <motion.article id="shipping-logistics" {...motionConfig} className="scroll-mt-24 rounded-xs border border-border bg-card p-6 md:p-8">
              <div className="flex items-center justify-between border-b border-border pb-4 mb-5">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm font-bold text-primary">04</span>
                  <h2 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-wide text-foreground">
                    Shipping & freight logistics
                  </h2>
                </div>
                <Badge variant="outline" className="font-mono text-[10px] text-muted-foreground border-border">
                  SEC-04
                </Badge>
              </div>

              <div className="space-y-4 text-sm text-muted-foreground leading-relaxed font-sans">
                <p>
                  Huga Scalet fulfills and ships equipment nationwide across the United States from regional
                  distribution hubs. Standard ground logistics handle small to medium tools, while heavy machinery,
                  hydraulic lifts, and bulk garage units ship via LTL common carrier freight.
                </p>
                <p>
                  Title and risk of loss pass to the buyer upon tender of goods to the commercial freight carrier
                  (FOB Origin). Buyers must inspect crates upon arrival and document any exterior transit damage on
                  the bill of lading before signing.
                </p>

                <div className="bg-card border border-border p-4 rounded-xs flex items-start gap-3">
                  <Truck className="size-5 text-primary shrink-0 mt-0.5" />
                  <div className="text-xs space-y-1">
                    <p className="font-semibold text-foreground">Freight Delivery Requirement</p>
                    <p className="text-muted-foreground">
                      Liftgate services and dockside delivery must be confirmed at checkout for shipments exceeding
                      150 lbs.
                    </p>
                  </div>
                </div>
              </div>
            </motion.article>

            {/* Clause 05 */}
            <motion.article id="warranty-liability" {...motionConfig} className="scroll-mt-24 rounded-xs border border-border bg-card p-6 md:p-8">
              <div className="flex items-center justify-between border-b border-border pb-4 mb-5">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm font-bold text-primary">05</span>
                  <h2 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-wide text-foreground">
                    Warranty & liability limits
                  </h2>
                </div>
                <Badge variant="outline" className="font-mono text-[10px] text-muted-foreground border-border">
                  SEC-05
                </Badge>
              </div>

              <div className="space-y-4 text-sm text-muted-foreground leading-relaxed font-sans">
                <p>
                  All catalog items carry standard original manufacturer warranties against material defects. Huga
                  Scalet LLC provides equipment &quot;as is&quot; beyond manufacturer warranties without additional
                  express or implied warranties, including merchantability or fitness for a particular workshop
                  operation.
                </p>

                <div className="bg-muted/40 border border-border p-4 rounded-xs space-y-2">
                  <div className="flex items-center gap-2">
                    <ShieldAlert className="size-4 text-primary" />
                    <span className="font-mono text-xs font-bold uppercase tracking-wider text-foreground">
                      Liability ceiling disclaimer
                    </span>
                  </div>
                  <p className="font-mono text-xs text-muted-foreground leading-relaxed uppercase">
                    IN NO EVENT SHALL HUGA SCALET LLC BE LIABLE FOR DIRECT, INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL,
                    OR CONSEQUENTIAL DAMAGES ARISING OUT OF THE MISUSE, UNTRAINED OPERATION, OR FIELD MODIFICATION OF
                    ANY TOOLS OR WORKSHOP GEAR SOLD ON THIS SITE. MAXIMUM AGGREGATE LIABILITY SHALL NOT EXCEED THE TOTAL
                    SUM ACTUALLY PAID FOR THE SPECIFIC ITEM GIVING RISE TO THE CLAIM.
                  </p>
                </div>
              </div>
            </motion.article>

            {/* Clause 06 */}
            <motion.article id="dispute-resolution" {...motionConfig} className="scroll-mt-24 rounded-xs border border-border bg-card p-6 md:p-8">
              <div className="flex items-center justify-between border-b border-border pb-4 mb-5">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm font-bold text-primary">06</span>
                  <h2 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-wide text-foreground">
                    Dispute resolution & governing law
                  </h2>
                </div>
                <Badge variant="outline" className="font-mono text-[10px] text-muted-foreground border-border">
                  SEC-06
                </Badge>
              </div>

              <div className="space-y-4 text-sm text-muted-foreground leading-relaxed font-sans">
                <p>
                  These Terms and any purchase contracts shall be construed in accordance with the laws of the State
                  of California, USA, without regard to conflict of law principles.
                </p>
                <p>
                  Any dispute, controversy, or claim arising from these terms or equipment performance shall be
                  settled first through informal good-faith negotiation with Huga Scalet compliance officers. If
                  unresolved within 30 days, matters shall be submitted to binding arbitration in Orange County,
                  California.
                </p>

                <div className="flex items-center gap-3 p-3 bg-muted/20 border border-border rounded-xs">
                  <Scale className="size-4 text-primary shrink-0" />
                  <span className="font-mono text-xs text-muted-foreground">
                    Arbitration venue: Orange County Superior Jurisdiction, CA
                  </span>
                </div>
              </div>
            </motion.article>

            {/* Concluding Compliance & Contact Box */}
            <motion.div {...motionConfig} className="rounded-xs border border-border bg-card p-6 md:p-8 space-y-6">
              <div className="border-l-2 border-primary pl-4">
                <h3 className="font-serif text-lg md:text-xl font-bold uppercase tracking-wide text-foreground">
                  Compliance & legal assistance
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  For corporate requisitions, warranty claims, or legal inquiries, contact our depot compliance desk.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div className="p-3.5 bg-muted/30 border border-border rounded-xs space-y-1.5">
                  <div className="flex items-center gap-2 text-foreground font-mono text-xs font-semibold">
                    <Building2 className="size-4 text-primary" />
                    <span>Registered Depot</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Huga Scalet LLC</p>
                  <p className="text-xs text-muted-foreground">
                    2000 W Palmyra Ave, Ste 28, Orange, CA 92868, USA
                  </p>
                </div>

                <div className="p-3.5 bg-muted/30 border border-border rounded-xs space-y-1.5">
                  <div className="flex items-center gap-2 text-foreground font-mono text-xs font-semibold">
                    <Mail className="size-4 text-primary" />
                    <span>Direct Inquiries</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <a href="mailto:HugaScalet@proton.me" className="hover:text-primary transition-colors underline-offset-2 hover:underline">
                      HugaScalet@proton.me
                    </a>
                  </p>
                  <p className="text-[11px] text-muted-foreground font-mono">Response within 1 business day</p>
                </div>

                <div className="p-3.5 bg-muted/30 border border-border rounded-xs space-y-1.5">
                  <div className="flex items-center gap-2 text-foreground font-mono text-xs font-semibold">
                    <Phone className="size-4 text-primary" />
                    <span>Direct Telephone</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <a href="tel:+14842918653" className="hover:text-primary transition-colors underline-offset-2 hover:underline">
                      +1 (484) 291-8653
                    </a>
                  </p>
                  <p className="text-[11px] text-muted-foreground font-mono">Mon–Fri 8:00 AM – 5:00 PM PST</p>
                </div>
              </div>

              <Separator className="bg-border" />

              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <MapPin className="size-3.5 text-primary" />
                  <span>Orange, California, United States</span>
                </div>
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="sm" asChild className="font-serif uppercase text-xs tracking-wider rounded-xs border-border">
                    <Link to="/PrivacyPolicyPage">Privacy policy</Link>
                  </Button>
                  <Button variant="default" size="sm" asChild className="font-serif uppercase text-xs tracking-wider rounded-xs bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link to="/ContactPage">Contact support</Link>
                  </Button>
                </div>
              </div>
            </motion.div>
          </main>
        </div>
      </div>
    </section>;
}